import { BusinessContext } from '@periscopes/general/context';
import { FieldInformation } from './FieldInformation';
import { FieldTags } from './FieldTags';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { When } from 'react-if';

import { FormulaFieldMetaData, LookupFieldMetaData } from '@periscopes/general/fields';
import { PeriscopeSfFieldMetadata, SonarObjectType } from '@sonar-software/sonar-ts-types';
import React, { useEffect } from 'react';
import { usePeriscopeMetadata, useSonarObjectReference, useUpdateSonarObjectReference } from '@hooks';

export interface FieldMetadataTabProps {
	sonarObjectId: number;
	index: number;
}

export const FieldMetadataTab: React.FC<FieldMetadataTabProps> = ({ sonarObjectId, index }) => {
	const tagIds = useSonarObjectReference(sonarObjectId)?.tagIds ?? [];
	const { update } = useUpdateSonarObjectReference();
	const { loading, metadata } = usePeriscopeMetadata<PeriscopeSfFieldMetadata>(
		sonarObjectId,
		SonarObjectType.SalesforceField
	);

	useEffect(() => {
		if (metadata && sonarObjectId) {
			update({
				internalName: metadata.internalName,
				linkedIntegrations: metadata.linkedIntegrations,
				parentDisplayName: metadata.parentDisplayName,
				parentInternalName: metadata.parentInternalName,
				parentSonarObjectId: metadata.parentSonarObjectId,
				rawSalesforceType: metadata.rawSalesforceType,
				salesforce: {
					complianceCategory: metadata.complianceCategory,
					dataSensitivityLevelId: metadata.dataSensitivityLevelId,
					fieldUsageId: metadata.fieldUsageId,
					helpText: metadata.helpText,
					isCustom: metadata.isCustom
				},
				sonarObjectId
			});

			if (metadata.parentSonarObjectId) {
				update({
					displayName: metadata.parentDisplayName,
					sonarObjectId: metadata.parentSonarObjectId
				});
			}
		}
	}, [ metadata, sonarObjectId, update ]);

	if (!metadata || loading) {
		return <LoadingSpinner spinning />;
	}

	return (
		<>
			<FieldInformation sonarObjectId={ sonarObjectId } />
			<FormulaFieldMetaData
				formulaFieldMetaData={ metadata.formulaFieldReferences }
				index={ index } />
			<LookupFieldMetaData
				index={ index }
				lookupEntities={ metadata.lookupEntities } />
			<When condition={ tagIds.length }>
				<FieldTags
					index={ index }
					tagIds={ tagIds } />
			</When>
			<BusinessContext sonarObjectId={ sonarObjectId } />
		</>
	);
};
